(() => {
  if (window.__hundiniFellizSpendencodeLoaded) {
    return;
  }

  window.__hundiniFellizSpendencodeLoaded = true;

  /*
   * ============================================================
   * FELLiz KONFIGURATION
   * ============================================================
   */

  const FELLIZ_VARIANT_ID = 58189829734664;

  const VALID_CODE = 'FELLIZ5';
  const DISPLAY_CODE = 'FELLiz5';

  // CHF 50.00 = 5000 Rappen
  const MINIMUM_CART_TOTAL = 5000;

  const NOTICE_STORAGE_KEY = 'hundini-felliz-notice';

  let validationTimer = null;
  let validationRunning = false;
  let checkoutValidationBypass = false;


  /*
   * ============================================================
   * SHOPIFY CART API
   * ============================================================
   */

  const shopifyRoot = () => {
    return window.Shopify?.routes?.root || '/';
  };

  const cartUrl = (endpoint) => {
    return `${shopifyRoot()}${endpoint}`;
  };

  async function request(endpoint, options = {}) {
    const {
      headers = {},
      ...restOptions
    } = options;

    const defaultHeaders = {
      Accept: 'application/json'
    };

    if (restOptions.body) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const response = await fetch(cartUrl(endpoint), {
      credentials: 'same-origin',
      ...restOptions,
      headers: {
        ...defaultHeaders,
        ...headers
      }
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Shopify cart request failed.'
      );
    }

    return data;
  }


  async function getCart() {
    return request('cart.js', {
      method: 'GET'
    });
  }


  /*
   * ============================================================
   * FELLiz POSITION FINDEN
   * ============================================================
   */

  function getFellizItems(cart) {
    return (cart.items || []).filter(
      (item) => Number(item.variant_id) === FELLIZ_VARIANT_ID
    );
  }


  /*
   * ============================================================
   * GUTSCHEIN-PRÜFUNG
   * ============================================================
   *
   * Nicht erlaubt:
   *
   * - echte Shopify Gift Cards
   * - jedes Produkt, dessen Name "Gutschein" enthält
   *
   * Groß-/Kleinschreibung spielt keine Rolle.
   * ============================================================
   */

  function cartContainsGiftVoucher(cart) {
    return (cart.items || []).some((item) => {
      /*
       * Unsere FELLiz-Position selbst ignorieren.
       */
      if (Number(item.variant_id) === FELLIZ_VARIANT_ID) {
        return false;
      }

      const title = String(
        item.product_title ||
        item.title ||
        ''
      ).toLowerCase();

      return (
        item.gift_card === true ||
        title.includes('gutschein')
      );
    });
  }


  /*
   * ============================================================
   * RABATT-PRÜFUNG
   * ============================================================
   *
   * Andere Produkt-/Bestellrabatte sind NICHT erlaubt.
   *
   * Wir prüfen bewusst den Rabattbetrag des Warenkorbs.
   *
   * Versandrabatte werden nicht als Konflikt behandelt.
   * ============================================================
   */

  function cartHasOtherDiscount(cart) {
    return Number(cart.total_discount || 0) > 0;
  }


  /*
   * ============================================================
   * MINDESTWARENKORBWERT
   * ============================================================
   *
   * CHF 50 nach bereits im Cart berücksichtigten Rabatten.
   * ============================================================
   */

  function cartMeetsMinimum(cart) {
    return Number(cart.total_price || 0) >= MINIMUM_CART_TOTAL;
  }


  /*
   * ============================================================
   * GESAMTE GÜLTIGKEITSPRÜFUNG
   * ============================================================
   */

  function getEligibility(cart) {
    /*
     * 1. Gutscheine haben immer Vorrang.
     */
    if (cartContainsGiftVoucher(cart)) {
      return {
        valid: false,
        reason: 'gift'
      };
    }

    /*
     * 2. Keine anderen Produkt-/Bestellrabatte.
     */
    if (cartHasOtherDiscount(cart)) {
      return {
        valid: false,
        reason: 'discount'
      };
    }

    /*
     * 3. Mindestens CHF 50.
     */
    if (!cartMeetsMinimum(cart)) {
      return {
        valid: false,
        reason: 'minimum'
      };
    }

    return {
      valid: true,
      reason: null
    };
  }


  /*
   * ============================================================
   * FELLiz POSITION HINZUFÜGEN
   * ============================================================
   */

  async function addFellizItem() {
    await request('cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [
          {
            id: FELLIZ_VARIANT_ID,
            quantity: 1,
            properties: {
              'FELLiz Code': DISPLAY_CODE
            }
          }
        ]
      })
    });
  }


  /*
   * ============================================================
   * FELLiz POSITION ENTFERNEN
   * ============================================================
   */

  async function removeFellizItems(items) {
    if (!items || !items.length) {
      return;
    }

    const updates = {};

    items.forEach((item) => {
      updates[item.key] = 0;
    });

    await request('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({
        updates
      })
    });
  }


  /*
   * ============================================================
   * FELLiz POSITION NORMALISIEREN
   * ============================================================
   *
   * Garantiert:
   *
   * - maximal eine FELLiz Position
   * - Menge immer 1
   * - FELLiz Code als Line Item Property
   * ============================================================
   */

  async function normalizeFellizItems(cart) {
    let fellizItems = getFellizItems(cart);

    if (!fellizItems.length) {
      return;
    }

    /*
     * Mehrere FELLiz Positionen bereinigen.
     */
    if (fellizItems.length > 1) {
      const updates = {};

      fellizItems.forEach((item, index) => {
        updates[item.key] = index === 0 ? 1 : 0;
      });

      await request('cart/update.js', {
        method: 'POST',
        body: JSON.stringify({
          updates
        })
      });

      cart = await getCart();
      fellizItems = getFellizItems(cart);
    }

    const fellizItem = fellizItems[0];

    if (!fellizItem) {
      return;
    }

    const currentProperties = fellizItem.properties || {};

    const needsUpdate =
      Number(fellizItem.quantity) !== 1 ||
      currentProperties['FELLiz Code'] !== DISPLAY_CODE;

    if (!needsUpdate) {
      return;
    }

    await request('cart/change.js', {
      method: 'POST',
      body: JSON.stringify({
        id: fellizItem.key,
        quantity: 1,
        properties: {
          ...currentProperties,
          'FELLiz Code': DISPLAY_CODE
        }
      })
    });
  }


  /*
   * ============================================================
   * CODE ANWENDEN
   * ============================================================
   */

  async function applyFellizCode() {
    const cart = await getCart();

    const eligibility = getEligibility(cart);

    if (!eligibility.valid) {
      return {
        success: false,
        reason: eligibility.reason
      };
    }

    const fellizItems = getFellizItems(cart);

    if (fellizItems.length === 0) {
      await addFellizItem();
    } else {
      await normalizeFellizItems(cart);
    }

    return {
      success: true,
      reason: null
    };
  }


  /*
   * ============================================================
   * CODE MANUELL ENTFERNEN
   * ============================================================
   */

  async function removeFellizCode() {
    const cart = await getCart();

    const fellizItems = getFellizItems(cart);

    if (!fellizItems.length) {
      return;
    }

    await removeFellizItems(fellizItems);
  }


  /*
   * ============================================================
   * UI
   * ============================================================
   */

  function setLoading(container, loading) {
    if (!container) {
      return;
    }

    container.classList.toggle(
      'is-loading',
      loading
    );

    container.setAttribute(
      'aria-busy',
      loading ? 'true' : 'false'
    );

    container
      .querySelectorAll('button, input')
      .forEach((element) => {
        element.disabled = loading;
      });
  }


  function clearMessage(container) {
    const messageElement = container?.querySelector(
      '[data-felliz-message]'
    );

    if (!messageElement) {
      return;
    }

    messageElement.textContent = '';

    messageElement.classList.remove(
      'is-visible',
      'is-error'
    );
  }


  function showError(container, message) {
    const messageElement = container?.querySelector(
      '[data-felliz-message]'
    );

    if (!messageElement) {
      return;
    }

    messageElement.textContent = message;

    messageElement.classList.add(
      'is-visible',
      'is-error'
    );
  }


  function getMessageForReason(container, reason) {
    if (!container) {
      return '';
    }

    switch (reason) {
      case 'gift':
        return (
          container.dataset.giftMessage ||
          'Der Spendencode FELLiz5 kann nicht beim Kauf von Gutscheinen angewendet werden.'
        );

      case 'discount':
        return (
          container.dataset.discountMessage ||
          'Der Spendencode FELLiz5 kann nicht mit anderen Rabatten kombiniert werden. Versandrabatte sind davon ausgenommen.'
        );

      case 'minimum':
        return (
          container.dataset.minimumMessage ||
          'Der Spendencode FELLiz5 ist ab einem Warenkorbwert von CHF 50 gültig.'
        );

      default:
        return (
          container.dataset.errorMessage ||
          'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.'
        );
    }
  }


  /*
   * ============================================================
   * MELDUNG NACH CART REFRESH / RELOAD MERKEN
   * ============================================================
   */

  function storeNotice(reason) {
    try {
      sessionStorage.setItem(
        NOTICE_STORAGE_KEY,
        reason
      );
    } catch (error) {
      // Session Storage ist nur Komfort.
    }
  }


  function getStoredNotice() {
    try {
      return sessionStorage.getItem(
        NOTICE_STORAGE_KEY
      );
    } catch (error) {
      return null;
    }
  }


  function clearStoredNotice() {
    try {
      sessionStorage.removeItem(
        NOTICE_STORAGE_KEY
      );
    } catch (error) {
      // Nichts weiter nötig.
    }
  }


  function showStoredNotice() {
    const reason = getStoredNotice();

    if (!reason) {
      return;
    }

    const containers = document.querySelectorAll(
      '[data-felliz-code][data-felliz-active="false"]'
    );

    if (!containers.length) {
      return;
    }

    containers.forEach((container) => {
      showError(
        container,
        getMessageForReason(container, reason)
      );
    });

    clearStoredNotice();
  }


  /*
   * ============================================================
   * PRESTIGE CART UI AKTUALISIEREN
   * ============================================================
   */

  function refreshCartInterface() {
    /*
     * Auf der vollständigen /cart Seite:
     * kompletter Reload.
     */
    if (
      document.querySelector(
        '.shopify-section--main-cart'
      )
    ) {
      window.location.reload();
      return;
    }

    /*
     * Cart Drawer:
     * Prestige UI aktualisieren.
     */
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true
      })
    );

    /*
     * Eventuell gespeicherte Fehlermeldung
     * nach dem Refresh wieder anzeigen.
     */
    window.setTimeout(
      showStoredNotice,
      700
    );
  }


  /*
   * ============================================================
   * AKTIVEN FELLiz CODE ERNEUT PRÜFEN
   * ============================================================
   *
   * Beispiel:
   *
   * FELLiz5 aktiv bei CHF 70
   *
   * danach:
   * - Gutschein hinzugefügt
   * - Rabatt angewendet
   * - Warenkorb unter CHF 50 reduziert
   *
   * => FELLiz wird automatisch entfernt.
   * ============================================================
   */

  async function validateActiveFellizCode(
    suppliedCart = null
  ) {
    if (validationRunning) {
      return;
    }

    validationRunning = true;

    try {
      const cart =
        suppliedCart &&
        Array.isArray(suppliedCart.items)
          ? suppliedCart
          : await getCart();

      const fellizItems = getFellizItems(cart);

      if (!fellizItems.length) {
        return;
      }

      const eligibility = getEligibility(cart);

      /*
       * Code ist nicht mehr gültig.
       */
      if (!eligibility.valid) {
        storeNotice(
          eligibility.reason
        );

        await removeFellizItems(
          fellizItems
        );

        refreshCartInterface();

        return;
      }

      /*
       * Defensive Prüfung:
       * FELLiz muss immer genau einmal
       * mit Menge 1 vorhanden sein.
       */
      const fellizItem = fellizItems[0];

      const fellizProperties =
        fellizItem?.properties || {};

      const needsNormalization =
        fellizItems.length !== 1 ||
        Number(fellizItem?.quantity) !== 1 ||
        fellizProperties['FELLiz Code'] !==
          DISPLAY_CODE;

      if (needsNormalization) {
        await normalizeFellizItems(cart);

        refreshCartInterface();
      }

    } catch (error) {
      console.error(
        'FELLiz Validierung:',
        error
      );

    } finally {
      validationRunning = false;
    }
  }


  /*
   * Validierungen bündeln, damit bei mehreren
   * Cart Events nicht mehrere Requests gleichzeitig laufen.
   */
  function scheduleValidation(cart = null) {
    window.clearTimeout(
      validationTimer
    );

    validationTimer = window.setTimeout(
      () => {
        validateActiveFellizCode(cart);
      },
      300
    );
  }


  /*
   * ============================================================
   * EINLÖSEN
   * ============================================================
   */

  async function handleApply(container) {
    const input = container.querySelector(
      '[data-felliz-input]'
    );

    if (!input) {
      return;
    }

    clearMessage(container);

    const enteredCode = input.value
      .trim()
      .replace(/\s+/g, '')
      .toUpperCase();

    /*
     * Falscher Code.
     */
    if (enteredCode !== VALID_CODE) {
      showError(
        container,
        container.dataset.invalidMessage ||
          'Dieser Spendencode ist nicht gültig.'
      );

      input.focus();

      return;
    }

    setLoading(
      container,
      true
    );

    try {
      const result =
        await applyFellizCode();

      /*
       * Code selbst stimmt,
       * Bedingungen aber nicht.
       */
      if (!result.success) {
        setLoading(
          container,
          false
        );

        showError(
          container,
          getMessageForReason(
            container,
            result.reason
          )
        );

        return;
      }

      /*
       * Erfolgreich.
       */
      refreshCartInterface();

    } catch (error) {
      console.error(
        'FELLiz Spendencode:',
        error
      );

      setLoading(
        container,
        false
      );

      showError(
        container,
        container.dataset.errorMessage ||
          'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.'
      );
    }
  }


  /*
   * ============================================================
   * MANUELL ENTFERNEN
   * ============================================================
   */

  async function handleRemove(container) {
    setLoading(
      container,
      true
    );

    try {
      await removeFellizCode();

      refreshCartInterface();

    } catch (error) {
      console.error(
        'FELLiz Spendencode:',
        error
      );

      setLoading(
        container,
        false
      );
    }
  }


  /*
   * ============================================================
   * BUTTON EVENTS
   * ============================================================
   */

  document.addEventListener(
    'click',
    (event) => {
      const applyButton =
        event.target.closest(
          '[data-felliz-apply]'
        );

      if (applyButton) {
        const container =
          applyButton.closest(
            '[data-felliz-code]'
          );

        if (container) {
          handleApply(container);
        }

        return;
      }

      const removeButton =
        event.target.closest(
          '[data-felliz-remove]'
        );

      if (removeButton) {
        const container =
          removeButton.closest(
            '[data-felliz-code]'
          );

        if (container) {
          handleRemove(container);
        }
      }
    }
  );


  /*
   * ENTER im Spendencode-Feld.
   */
  document.addEventListener(
    'keydown',
    (event) => {
      const input =
        event.target.closest(
          '[data-felliz-input]'
        );

      if (
        !input ||
        event.key !== 'Enter'
      ) {
        return;
      }

      event.preventDefault();

      const container =
        input.closest(
          '[data-felliz-code]'
        );

      if (container) {
        handleApply(container);
      }
    }
  );


  /*
   * ============================================================
   * PRESTIGE CART EVENTS
   * ============================================================
   *
   * Wird ein Produkt hinzugefügt,
   * entfernt oder die Menge geändert,
   * prüfen wir FELLiz erneut.
   * ============================================================
   */

  document.addEventListener(
    'cart:change',
    (event) => {
      scheduleValidation(
        event.detail?.cart || null
      );
    }
  );


  /*
   * Auch nach einem Cart Refresh prüfen.
   *
   * Das hilft insbesondere bei Änderungen,
   * die nicht über die normale Mengensteuerung laufen.
   */
  document.documentElement.addEventListener(
    'cart:refresh',
    () => {
      scheduleValidation();
    }
  );


  /*
   * ============================================================
   * LETZTE PRÜFUNG VOR CHECKOUT
   * ============================================================
   *
   * Selbst wenn zwischenzeitlich ein Rabatt angewendet wurde,
   * prüfen wir unmittelbar vor dem Wechsel in den Checkout.
   * ============================================================
   */

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const checkoutButton =
        event.submitter?.name === 'checkout'
          ? event.submitter
          : form.querySelector(
              '[name="checkout"]'
            );

      if (!checkoutButton) {
        return;
      }

      /*
       * Zweiter Submit nach erfolgreicher Validierung.
       */
      if (checkoutValidationBypass) {
        checkoutValidationBypass = false;
        return;
      }

      /*
       * Kein FELLiz aktiv:
       * normales Shopify-Verhalten.
       */
      const activeContainer =
        document.querySelector(
          '[data-felliz-code][data-felliz-active="true"]'
        );

      if (!activeContainer) {
        return;
      }

      /*
       * Checkout kurz stoppen und aktuellsten
       * Warenkorb aus Shopify holen.
       */
      event.preventDefault();

      setLoading(
        activeContainer,
        true
      );

      try {
        const cart =
          await getCart();

        const fellizItems =
          getFellizItems(cart);

        /*
         * Position wurde zwischenzeitlich entfernt.
         * Checkout darf normal weiterlaufen.
         */
        if (!fellizItems.length) {
          checkoutValidationBypass = true;

          setLoading(
            activeContainer,
            false
          );

          if (form.requestSubmit) {
            form.requestSubmit(
              checkoutButton
            );
          } else {
            form.submit();
          }

          return;
        }

        const eligibility =
          getEligibility(cart);

        /*
         * FELLiz ist inzwischen ungültig.
         *
         * Dann entfernen wir FELLiz und lassen
         * den Kunden den Checkout anschließend
         * erneut starten.
         */
        if (!eligibility.valid) {
          storeNotice(
            eligibility.reason
          );

          await removeFellizItems(
            fellizItems
          );

          refreshCartInterface();

          return;
        }

        /*
         * Alles gültig:
         * Checkout freigeben.
         */
        checkoutValidationBypass = true;

        setLoading(
          activeContainer,
          false
        );

        if (form.requestSubmit) {
          form.requestSubmit(
            checkoutButton
          );
        } else {
          form.submit();
        }

      } catch (error) {
        console.error(
          'FELLiz Checkout-Validierung:',
          error
        );

        setLoading(
          activeContainer,
          false
        );

        const inactiveContainer =
          document.querySelector(
            '[data-felliz-code]'
          );

        if (inactiveContainer) {
          showError(
            inactiveContainer,
            inactiveContainer.dataset.errorMessage ||
              'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.'
          );
        }
      }
    },
    true
  );


  /*
   * ============================================================
   * INITIALISIERUNG
   * ============================================================
   */

  function initializeFelliz() {
    showStoredNotice();

    validateActiveFellizCode();
  }


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initializeFelliz,
      {
        once: true
      }
    );
  } else {
    initializeFelliz();
  }
})();